import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"
import { DEFAULT_SEASON_YEAR } from "utils/seasonYear"

const service = new UserService()

const GROUP_BY_OPTIONS = [
  "Senior Region",
  "State",
  "Sales Year",
  "Month of Blitz",
  "Team",
  "Management Group",
  "Senior Management Group",
  "Region",
  "Partnership",
]
const GROUP_BY_TO_API: Record<string, string> = {
  "Senior Region": "Senior Region",
  State: "State",
  "Sales Year": "Sales Year",
  "Month of Blitz": "Month of Blitz",
  Team: "Team",
  "Management Group": "Management Group",
  "Senior Management Group": "Senior Management Group",
  Region: "Region",
  Partnership: "Partnership",
}

type BlitzRow = {
  _id: string
  name: string
  count: number
  registered?: number
  unregistered?: number
  sales_rep?: number
  vets?: number
  rookies?: number
  techs_needed?: number
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
}: {
  value: number
  maxVal: number
  colorClass?: string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  return (
    <div className="flex items-center gap-2 min-w-[70px]">
      <span className="text-sm tabular-nums shrink-0 text-ink">{value.toLocaleString()}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export const BlitzReport = () => {
  const [date, setDate] = useState<string>("YEARTODATE")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [dateFrom, setDateFrom] = useState<string>("")
  const [dateTo, setDateTo] = useState<string>("")
  const [groupBy, setGroupBy] = useState<string>("Senior Region")
  const [isRegistered, setIsRegistered] = useState<string>("")
  const [tenure, setTenure] = useState<string>("")
  const [salesYear, setSalesYear] = useState<string>(DEFAULT_SEASON_YEAR)

  const [filterOptions, setFilterOptions] = useState<{
    tenures: string[]
    salesYears: string[]
  }>({ tenures: [], salesYears: [] })
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, BlitzRow[]> | null>(null)

  const groupByApi = groupBy ? (GROUP_BY_TO_API[groupBy] ?? "") : ""

  const tenureOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.tenures.map((s) => ({ value: s, label: s }))],
    [filterOptions.tenures]
  )
  const salesYearOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.salesYears.map((s) => ({ value: s, label: s }))],
    [filterOptions.salesYears]
  )

  const queryParams = useMemo(
    () => ({
      date: date || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      groupBy: groupByApi || undefined,
      isRegistered: isRegistered || undefined,
      tenure: tenure || undefined,
      salesYear: salesYear || undefined,
    }),
    [date, dateFrom, dateTo, groupByApi, isRegistered, tenure, salesYear]
  )

  const fetchReport = useCallback(() => {
    if (!groupByApi) return
    setLoading(true)
    service
      .getBlitzUsersRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
        else setApiData(null)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupByApi])

  const fetchFilterOptions = useCallback(() => {
    service.getBlitzFilterOptions().then((resp: any) => {
      const d = resp?.data?.data ?? resp?.data ?? {}
      setFilterOptions({
        tenures: Array.isArray(d.tenures) ? d.tenures : [],
        salesYears: Array.isArray(d.salesYears) ? d.salesYears : [],
      })
    })
  }, [])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupByApi) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupByApi, fetchReport])

  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [] as BlitzRow[], dataGroupBy: null }
    const keyForCurrent = groupByApi ? `by_${groupByApi}` : null
    const arr = keyForCurrent ? apiData[keyForCurrent] : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupByApi }
    }
    const firstKey = Object.keys(apiData)[0]
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? apiData[firstKey] : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, "") : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupByApi])

  const maxCount = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.count), 1) : 1), [tableRows])
  const maxRegistered = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.registered ?? 0), 1) : 1),
    [tableRows]
  )
  const maxUnregistered = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.unregistered ?? 0), 1) : 1),
    [tableRows]
  )
  const maxSalesRep = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.sales_rep ?? 0), 1) : 1),
    [tableRows]
  )
  const maxVets = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.vets ?? 0), 1) : 1), [tableRows])
  const maxRookies = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.rookies ?? 0), 1) : 1), [tableRows])
  const maxTech = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.techs_needed ?? 0), 1) : 1),
    [tableRows]
  )

  const totals = useMemo(
    () => ({
      count: tableRows.reduce((s, r) => s + (r.count ?? 0), 0),
      registered: tableRows.reduce((s, r) => s + (r.registered ?? 0), 0),
      unregistered: tableRows.reduce((s, r) => s + (r.unregistered ?? 0), 0),
      sales_rep: tableRows.reduce((s, r) => s + (r.sales_rep ?? 0), 0),
      vets: tableRows.reduce((s, r) => s + (r.vets ?? 0), 0),
      rookies: tableRows.reduce((s, r) => s + (r.rookies ?? 0), 0),
      tech: tableRows.reduce((s, r) => s + (r.techs_needed ?? 0), 0),
    }),
    [tableRows]
  )

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const colCount = 8

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Blitz Report</h1>
        <p className="page-subtitle">Blitz user/registration counts by group (Option B: by BlitzUsers).</p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date range</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") setDate(e.target.value)
                else setShowCustom(true)
              }}
              className="px-3 py-2 text-sm"
            >
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-56 rounded-lg border border-line bg-white text-ink px-3 text-sm"
                customInput={<input placeholder="Select date" className="rounded-lg border border-line bg-white text-ink w-56 py-2 px-3 text-sm" />}
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Blitz date from</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Blitz date to</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Registered</label>
            <select
              value={isRegistered}
              onChange={(e) => setIsRegistered(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Tenure</label>
            <select
              value={tenure}
              onChange={(e) => setTenure(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {tenureOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Sales year</label>
            <select
              value={salesYear}
              onChange={(e) => setSalesYear(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {salesYearOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupByApi || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {dataGroupBy && groupByApi && dataGroupBy !== groupByApi && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupByApi}</strong>.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Blitz users by {groupBy || "Group"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Total</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Registered</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Unregistered</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Vets</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Rookies</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Tech</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Sales Rep</th>
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select group by and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr
                    key={row._id != null ? String(row._id) : `row-${idx}`}
                    className="border-b border-line hover:bg-surface transition-colors"
                  >
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.name ?? row._id ?? "—"}</td>
                    <td className="px-2 py-3">
                      <BarCell value={row.count} maxVal={maxCount} colorClass="bg-blue-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.registered ?? 0} maxVal={maxRegistered} colorClass="bg-emerald-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.unregistered ?? 0} maxVal={maxUnregistered} colorClass="bg-amber-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.vets ?? 0} maxVal={maxVets} colorClass="bg-indigo-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rookies ?? 0} maxVal={maxRookies} colorClass="bg-cyan-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.techs_needed ?? 0} maxVal={maxTech} colorClass="bg-emerald-600" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.sales_rep ?? 0} maxVal={maxSalesRep} colorClass="bg-sky-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-2 py-3 whitespace-nowrap">Total</td>
                <td className="px-2 py-3">{totals.count.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.registered.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.unregistered.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.vets.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.rookies.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.tech.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.sales_rep.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
